<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import DialogRevamp from '@/components/DialogRevamp.vue'
import InputFieldFloatingWithMessage from '@/components/ui/input/InputFieldFloatingWithMessage.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseAttentionBox from '@/components/ui/attentionbox/BaseAttentionBox.vue'
import SocialLoginList from '@/components/SocialLoginList.vue'
import { getUserInfo } from '@/assets/ts/apiManagement'
import useAuthDialog from '@/lib/useAuthDialog'
import checkAuthMethod from '@/lib/checkAuthMethod.ts'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'
import injectStrict from '@/lib/inject'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import { checkIsPhActivation, getPhLayoutType } from '@/lib/checkService'
import { getCurrentGaPageName } from '@/lib/utils'

// in-app-solution
import { useAppOpener } from '@/lib/useAppOpener'

const props = defineProps({
	emailKeep: {
		type: String,
		default: '',
	},
	errorApiMessage: {
		type: String,
		default: '',
	},
	isExposeEntranceOnly: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits([
	'hide',
	'submit',
	'signInGoogle',
	'signInFacebook',
	'signInApple',
	'updateEmail',
])

// in-app-solution
const { isInAppBrowser, isJPRegion } = useAppOpener()
const { isShowDialog } = useAuthDialog(hideDialogDirectly)
const textContent: any = injectStrict('textContent')
const authMethod = checkAuthMethod()
const isLoading = ref(false)
const isPhActivation = checkIsPhActivation() && getPhLayoutType() === 'A'
const formState = reactive({
	email: props.emailKeep,
})
const rules = {
	email: {
		required,
		email,
	},
}
const $v = useVuelidate(rules, formState)
// basic, email-otp, google, facebook, apple
const lastLoginFrom = ref('')

const isSupportEmailLogin = computed(() => authMethod.isSupportEmailLogin)
const isSupportGoogleLogin = computed(() => authMethod.isSupportGoogleLogin)
const isSupportFacebookLogin = computed(() => authMethod.isSupportFacebookLogin)
const isSupportAppleLogin = computed(() => authMethod.isSupportAppleLogin)
const isLastLoginFromBasic = computed(
	() => lastLoginFrom.value === 'basic' || lastLoginFrom.value === 'email-otp',
)
const emailErrorMessage = computed(() => {
	if ($v.value.email.required.$invalid)
		return textContent.input_error_general_required
	if ($v.value.email.email.$invalid)
		return textContent.input_error_email_invalid
	return ''
})
const isErrorApiMessageExist = computed(() => props.errorApiMessage !== '')

// Determine whether to show social login buttons
// Condition: Not in an in-app browser OR not in Japan region
const shouldShowSocialLogin = computed(
	() => !(isInAppBrowser() && isJPRegion.value),
)

const isPhLayoutA = computed(() => isPhActivation && props.isExposeEntranceOnly)
const buttonEmail = computed(() => {
	if (isPhLayoutA.value) {
		return window.pageContent.heroContent?.layoutB?.buttonEmail || ''
	}
	return ''
})
const currentSignupFormTitle = computed(() => {
	if (isPhLayoutA.value) {
		return window.pageContent.heroContent?.layoutA?.formTitle || ''
	}
	return ''
})
const currentSignupFormSubtitle = computed(() => {
	if (isPhLayoutA.value) {
		const settings = window.BUYANDSHIP?.settings || {}
		const welcomeBonus = settings.member_welcome_credits || ''
		const currency = settings.current_country2.currency_symbol || ''

		const formSubtitle =
			window.pageContent.heroContent?.layoutA?.formSubtitle || ''
		return formSubtitle
			?.replace(/{WELCOME_CREDIT}/g, String(welcomeBonus))
			.replace(/{CURRENCY}/g, currency)
	}
	return ''
})

async function submit() {
	$v.value.$touch()
	if (isLoading.value || $v.value.$invalid) return
	isLoading.value = true
	const isEmailExist = await checkIsEmailExist()
	emit('submit', {
		type: isEmailExist ? 'login' : 'signup',
		email: formState.email,
	})
	const pageName = getCurrentGaPageName()
	createGAEvent('email_input_passed', {
		...(pageName && { page_name: pageName }),
		...(isPhLayoutA.value && { event_extra: 'Email - Hero' }),
	})
	if (isPhLayoutA.value) {
		createGAEvent(GaGeneralEvent.LoginSignupStart, {
			...(pageName && { page_name: pageName }),
			event_extra: 'Email - Hero',
		})
	}
	isLoading.value = false
}

async function checkIsEmailExist() {
	if (!formState.email) return false
	return getUserInfo(formState.email)
		.then(() => true)
		.catch(() => false)
}

function checkLastLoginMethod() {
	if (localStorage.getItem('lastLoginMethod')) {
		lastLoginFrom.value = localStorage.getItem('lastLoginMethod')!
	}
}

function signInGoogle() {
	emit('signInGoogle')
}

function signInFacebook() {
	emit('signInFacebook')
}

function signInApple() {
	emit('signInApple')
}

function sendEvent(name: string) {
	createGAEvent(name)
}

function hideDialogDirectly() {
	emit('hide')
}

watch(
	() => formState.email,
	() => {
		emit('updateEmail', formState.email)
	},
)

checkLastLoginMethod()
</script>

<template>
	<div>
		<div class="bs-auth-entrance__expose" v-if="isPhLayoutA">
			<div class="bs-auth-entrance__content">
				<h4 class="bs-auth-entrance__expose__title">
					{{ currentSignupFormTitle }}
				</h4>
				<div class="bs-auth-entrance__expose__subtitle">
					{{ currentSignupFormSubtitle }}
				</div>
				<BaseAttentionBox
					v-if="isErrorApiMessageExist"
					class="bs-auth__attention-box"
					:icon="'error'"
					:background="'error'"
				>
					<template #description>
						{{ errorApiMessage }}
					</template>
				</BaseAttentionBox>
				<template v-if="isSupportEmailLogin">
					<InputFieldFloatingWithMessage
						v-model="$v.email.$model"
						type="email"
						:disabled="isLoading"
						:label="textContent.input_label_email"
						:isError="$v.email.$error"
						:errorMessage="emailErrorMessage"
						:inputMode="'email'"
						:isLogin="isLastLoginFromBasic"
						:badgeText="textContent.tag_login_last_used"
					></InputFieldFloatingWithMessage>

					<BaseButton
						:isFull="true"
						:size="'large'"
						:isDisabled="isLoading"
						:isLoading="isLoading"
						class="bs-auth-entrance__content__submit-button"
						@click="submit"
					>
						{{ buttonEmail }}
					</BaseButton>

					<div class="bs-auth-entrance__separator">
						<span class="bs-auth-entrance__separator__center">
							{{ textContent.divider_or }}
						</span>
					</div>
				</template>

				<!-- Social login buttons section -->
				<div v-if="shouldShowSocialLogin">
					<SocialLoginList
						:isShowGoogle="isSupportGoogleLogin"
						:isShowFacebook="isSupportFacebookLogin"
						:isShowApple="isSupportAppleLogin"
						:tagText="textContent.tag_login_last_used"
						:tagType="lastLoginFrom"
						:isPhLayoutA="isPhLayoutA"
						@signInGoogle="signInGoogle"
						@signInFacebook="signInFacebook"
						@signInApple="signInApple"
					/>
				</div>

				<div class="bs-auth-entrance__content__remark">
					<template v-if="textContent.signup_remark_html">
						<span v-html="textContent.signup_remark_prefix"></span>
					</template>
					<template v-else>
						<span>{{ textContent.signup_remark_prefix }}</span>
						<a
							:href="`${getSiteBaseUrl()}/terms/`"
							@click="sendEvent('c_terms_f_signup')"
						>
							{{ textContent.signup_remark_term }}
						</a>
						<span>{{ textContent.signup_remark_and }}</span>
						<a
							:href="`${getSiteBaseUrl()}/privacy/`"
							@click="sendEvent('c_privacy_f_signup')"
						>
							{{ textContent.signup_remark_policy }}
						</a>
					</template>
				</div>
			</div>
		</div>

		<DialogRevamp
			v-else
			class="bs-auth-entrance"
			v-model="isShowDialog"
			:isFullInMobile="true"
			:size="'sm'"
			:isCustomOverlayFunction="true"
			:canCloseDialogByOverlay="false"
			:isCustomCloseFunction="true"
			@overlayClick="hideDialogDirectly"
			@defaultCloseClick="hideDialogDirectly"
		>
			<template #header>
				<h5 class="bs-auth__title">
					{{ textContent.login_signup_title }}
				</h5>
			</template>
			<template #content>
				<div class="bs-auth-entrance__content">
					<BaseAttentionBox
						v-if="isErrorApiMessageExist"
						class="bs-auth__attention-box"
						:icon="'error'"
						:background="'error'"
					>
						<template #description>
							{{ errorApiMessage }}
						</template>
					</BaseAttentionBox>
					<template v-if="isSupportEmailLogin">
						<InputFieldFloatingWithMessage
							v-model="$v.email.$model"
							type="email"
							:disabled="isLoading"
							:label="textContent.input_label_email"
							:isError="$v.email.$error"
							:errorMessage="emailErrorMessage"
							:inputMode="'email'"
							:isLogin="isLastLoginFromBasic"
							:badgeText="textContent.tag_login_last_used"
						></InputFieldFloatingWithMessage>

						<BaseButton
							:isFull="true"
							:size="'large'"
							:isDisabled="isLoading"
							:isLoading="isLoading"
							class="bs-auth-entrance__content__submit-button"
							@click="submit"
						>
							{{ textContent.button_next_step }}
						</BaseButton>

						<div class="bs-auth-entrance__separator">
							<span class="bs-auth-entrance__separator__center">
								{{ textContent.divider_or }}
							</span>
						</div>
					</template>

					<!-- Social login buttons section -->
					<div v-if="shouldShowSocialLogin">
						<SocialLoginList
							:isShowGoogle="isSupportGoogleLogin"
							:isShowFacebook="isSupportFacebookLogin"
							:isShowApple="isSupportAppleLogin"
							:tagText="textContent.tag_login_last_used"
							:tagType="lastLoginFrom"
							:isPhLayoutA="isPhLayoutA"
							@signInGoogle="signInGoogle"
							@signInFacebook="signInFacebook"
							@signInApple="signInApple"
						/>
					</div>

					<div class="bs-auth-entrance__content__remark">
						<template v-if="textContent.signup_remark_html">
							<span v-html="textContent.signup_remark_prefix"></span>
						</template>
						<template v-else>
							<span>{{ textContent.signup_remark_prefix }}</span>
							<a
								:href="`${getSiteBaseUrl()}/terms/`"
								@click="sendEvent('c_terms_f_signup')"
							>
								{{ textContent.signup_remark_term }}
							</a>
							<span>{{ textContent.signup_remark_and }}</span>
							<a
								:href="`${getSiteBaseUrl()}/privacy/`"
								@click="sendEvent('c_privacy_f_signup')"
							>
								{{ textContent.signup_remark_policy }}
							</a>
						</template>
					</div>
				</div>
			</template>
		</DialogRevamp>
	</div>
</template>
